import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { api, requeteConfigAuth } from '../api'
import { Icone } from '../composants/Icone'
import { estIos, estNavigateurIntegre, lienHorsNavigateurIntegre } from '../pwa/plateforme'

const ERREURS: Record<string, string> = {
  non_autorisee: "Cette adresse n'a pas accès à l'application.",
  annulee: 'Connexion annulée.',
  echec: 'La connexion a échoué. Réessayez.',
  indisponible: 'La connexion Google n’est pas configurée.',
}

/** Chemin local seulement : jamais de retour vers une autre origine. */
function retourSur(retour: string | undefined): string {
  return retour && /^\/(?![/\\])/.test(retour) ? retour : '/'
}

export function Connexion({
  erreur,
  retour,
}: {
  erreur?: string | undefined
  retour?: string | undefined
}) {
  const destination = retourSur(retour)
  const config = useQuery(requeteConfigAuth)
  const navigate = useNavigate()
  const client = useQueryClient()
  const [email, setEmail] = useState('')
  const [erreurDev, setErreurDev] = useState<string | null>(null)

  async function connexionDev(e: FormEvent) {
    e.preventDefault()
    const r = await api.auth.dev.$post({ json: { email } })
    if (!r.ok) {
      setErreurDev('Adresse non autorisée.')
      return
    }
    await client.invalidateQueries({ queryKey: ['auth'] })
    if (destination === '/') await navigate({ to: '/' })
    else window.location.assign(destination)
  }

  // Lien ouvert dans le navigateur d'une autre appli (Gmail, WhatsApp…) : Google y refuse la
  // connexion. On propose d'ouvrir la page demandée dans le vrai navigateur.
  const integre = estNavigateurIntegre(navigator)

  return (
    <div className="page-connexion">
      <main className="connexion">
        <span className="marque">
          <span className="marque-logo">
            <Icone nom="eco" plein taille={19} />
          </span>
          <span className="marque-texte">
            <strong>admin PMVB</strong>
            <span>Assistant administratif</span>
          </span>
        </span>
        <h1>Connexion</h1>
        <p className="discret">Échéances, paiements, pièces et tâches.</p>
        {erreur && <p className="alerte">{ERREURS[erreur] ?? ERREURS.echec}</p>}
        {integre && <HorsNavigateurIntegre destination={destination} />}

        {config.data?.google !== false && (
          <a
            className="bouton"
            href={
              destination === '/'
                ? '/api/auth/google'
                : `/api/auth/google?retour=${encodeURIComponent(destination)}`
            }
          >
            Se connecter avec Google
          </a>
        )}
        {config.data?.google === false && (
          <p className="discret">Connexion Google non configurée sur ce serveur.</p>
        )}

        {config.data?.dev && (
          <form className="carte" onSubmit={connexionDev}>
            <label htmlFor="email">Connexion de développement</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="adresse@exemple.invalid"
              required
            />
            <button type="submit" className="bouton">
              Entrer
            </button>
            {erreurDev && <p className="alerte">{erreurDev}</p>}
          </form>
        )}
      </main>
    </div>
  )
}

function HorsNavigateurIntegre({ destination }: { destination: string }) {
  const [copie, setCopie] = useState(false)
  const adresse = `${window.location.origin}${destination}`
  const lien = lienHorsNavigateurIntegre(adresse, navigator)
  const navigateur = estIos(navigator) ? 'Safari' : 'le navigateur'
  return (
    <div className="alerte">
      <p>
        Cette page s’est ouverte dans le navigateur d’une autre appli, où Google refuse la
        connexion. Ouvrez-la dans {navigateur} : le lien mène droit à ce qui était demandé.
      </p>
      <div className="boutons">
        {lien && (
          <a className="bouton" href={lien}>
            Ouvrir dans {navigateur}
          </a>
        )}
        <button
          type="button"
          className="bouton-discret"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(adresse)
              setCopie(true)
            } catch {
              setCopie(false)
            }
          }}
        >
          {copie ? 'Lien copié' : 'Copier le lien'}
        </button>
      </div>
      {estIos(navigator) && (
        <p className="discret">Sinon : menu de l’appli (···), puis « Ouvrir dans Safari ».</p>
      )}
    </div>
  )
}
